"use client";
import React, { useState } from "react";
import { Layers, Plus, Trash2, CheckCircle2 } from "lucide-react";

export const InteractiveService = () => {
    const [serviceName, setServiceName] = useState("UserService");
    const [description, setDescription] = useState("Handles user-related business logic");
    const [functions, setFunctions] = useState([
        "createUser",
        "getUserById",
        "updateUserProfile"
    ]);
    const [dependencies, setDependencies] = useState([
        "EmailService",
        "AuthService"
    ]);

    const addFunction = () => {
        setFunctions([...functions, `newFunction${functions.length + 1}`]);
    };

    const removeFunction = (index: number) => {
        setFunctions(functions.filter((_, i) => i !== index));
    };

    return (
        <div className="w-full max-w-2xl mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Layers className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">Service Builder</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 bg-gradient-to-b from-surface to-background">
                {/* Service Name */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Service Name
                    </label>
                    <input
                        type="text"
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        rows={2}
                    />
                </div>

                {/* Functions */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                            Service Functions
                        </label>
                        <button
                            onClick={addFunction}
                            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                            Add Function
                        </button>
                    </div>
                    <div className="space-y-2">
                        {functions.map((fn, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2 p-3 bg-background border border-border rounded-lg group hover:border-primary/30 transition-all"
                            >
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                <span className="flex-1 font-mono text-sm text-primary-text">{fn}()</span>
                                <button
                                    onClick={() => removeFunction(index)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-4 h-4 text-red-500 hover:text-red-400" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Dependencies */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Service Dependencies
                    </label>
                    <div className="space-y-2">
                        {dependencies.map((dep, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg"
                            >
                                <Layers className="w-4 h-4 text-primary shrink-0" />
                                <span className="flex-1 font-mono text-sm text-primary-text">{dep}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-background/80 rounded-lg border border-border/50">
                    <div className="text-xs font-medium text-secondary-text uppercase tracking-wider mb-2">
                        Class Preview
                    </div>
                    <div className="font-mono text-xs space-y-1 text-primary-text">
                        <div className="text-purple-400">export class</div>
                        <div className="ml-4 text-green-400">{serviceName}</div>
                        <div className="text-secondary-text">{"{"}</div>
                        <div className="ml-4 text-secondary-text">// {functions.length} methods</div>
                        <div className="ml-4 text-secondary-text">// {dependencies.length} dependencies</div>
                        <div className="text-secondary-text">{"}"}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
